/**
 * The swatch that picks a stage's or a note shortcut's colour, opening a
 * grid of the colours themselves.
 *
 * Used by: StageSettings and NoteShortcutSettings (Profile > Labels). The
 * colours are the keys of shared/theme/colorPalette, saved as their names.
 *
 * @module ColorPicker
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { COLOR_KEYS, COLOR_THEMES, type ColorKey } from '@/shared/theme/colorPalette';

/** The colour chosen, where a new one goes, and what it is the colour of. */
interface ColorPickerProps {
  /** The colour's key as saved; anything unknown shows as green. */
  value: string;
  /** Called with the colour tapped; the grid then closes. */
  onChange: (key: ColorKey) => void;
  /** Labels the swatch for a screen reader, which sees no colour at all. */
  describedThing: string;
}

/**
 * The colours a stage or a shortcut can be painted, shown as themselves.
 *
 * A colour is the one setting that cannot be described in words better than it
 * can be shown, so this is a grid of the actual colours rather than a dropdown
 * of their names. The names still travel with them for anyone reading the page
 * by ear.
 */
export const ColorPicker: React.FC<ColorPickerProps> = ({
  value, onChange, describedThing,
}) => {
  const { reactTranslate: t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const current = (COLOR_KEYS as readonly string[]).includes(value)
    ? (value as ColorKey)
    : 'green';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={t('profile.labels.chooseColour', { thing: describedThing })}
        aria-expanded={isOpen}
        className={cn(
          'h-6 w-6 shrink-0 rounded-full ring-1 ring-inset ring-black/10 transition-transform hover:scale-110 dark:ring-white/20',
          COLOR_THEMES[current].swatch
        )}
      />

      {isOpen && (
        <>
          {/* Catches the click that dismisses the grid, so choosing a colour
              does not need a second tap somewhere precise. */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute left-0 top-8 z-20 grid w-48 grid-cols-6 gap-1.5 rounded-md border border-border bg-card p-2 shadow-lg">
            {COLOR_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => { onChange(key); setIsOpen(false); }}
                aria-label={key}
                aria-current={key === current}
                className={cn(
                  'h-6 w-6 rounded-full ring-1 ring-inset ring-black/10 transition-transform hover:scale-110 dark:ring-white/20',
                  COLOR_THEMES[key].swatch,
                  key === current && 'ring-2 ring-offset-2 ring-ring ring-offset-card dark:ring-ring'
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
